import { useEffect, useState } from "react";
import { Youtube, Calendar } from "lucide-react";
import { api } from "../lib/api";

const PRIVACY_OPTIONS = [
  { key: "private", label: "Private" },
  { key: "public", label: "Public" },
  { key: "schedule", label: "Schedule" },
];

export const YoutubePublishOptions = ({ kind, slot, value, onChange, testidPrefix = "publish-opt" }) => {
  const [connected, setConnected] = useState(true);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .get(`/youtube/auth/status?slot=${slot}`)
      .then((res) => setConnected(!!res.data.connected))
      .catch(() => {})
      .finally(() => setChecked(true));
  }, [slot]);

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div data-testid={`${testidPrefix}-container`} className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`${testidPrefix}-toggle`}
          checked={value.auto_publish}
          onChange={(e) => set({ auto_publish: e.target.checked })}
          className="w-4 h-4 accent-red-500"
        />
        <span className="text-sm text-slate-200 flex items-center gap-1.5">
          <Youtube className="w-4 h-4 text-red-500" /> Upload to YouTube once ready
        </span>
      </label>

      {value.auto_publish && (
        <div className="pl-1 space-y-3 animate-in fade-in">
          {checked && !connected && (
            <p data-testid={`${testidPrefix}-not-connected-warning`} className="text-xs text-amber-400">
              Connect this tool's YouTube account above so it can upload automatically.
            </p>
          )}
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Privacy</label>
            <div className="flex gap-2">
              {PRIVACY_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  data-testid={`${testidPrefix}-privacy-${o.key}`}
                  onClick={() => set({ publish_privacy: o.key })}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs border transition-all ${
                    value.publish_privacy === o.key
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-slate-900 border-slate-800 text-slate-300"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {value.publish_privacy === "schedule" && kind === "video" && (
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Publish date &amp; time
              </label>
              <input
                type="datetime-local"
                data-testid={`${testidPrefix}-datetime`}
                value={value.publish_at || ""}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => set({ publish_at: e.target.value })}
                className="w-full bg-slate-900 border border-slate-800 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500"
              />
            </div>
          )}

          {value.publish_privacy === "schedule" && kind === "song" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Landscape video(s) at
                </label>
                <input
                  type="datetime-local"
                  data-testid={`${testidPrefix}-datetime-landscape`}
                  value={value.publish_at_landscape || ""}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => set({ publish_at_landscape: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Shorts video(s) at
                </label>
                <input
                  type="datetime-local"
                  data-testid={`${testidPrefix}-datetime-shorts`}
                  value={value.publish_at_shorts || ""}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => set({ publish_at_shorts: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
